/**
 * Spatial Processing Stage
 *
 * Creates spatial buckets for each lap to enable distance-based analysis
 * independent of time-based sampling rate.
 *
 * This stage must run after LapProcessingStage and adds spatial buckets
 * to each Lap object.
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { Lap } from '../../../domain/lap/Lap'
import { LapMetadata } from '../../../domain/lap/LapMetadata'
import { createSpatialBuckets } from '../../spatial/spatialProcessing'

export class SpatialProcessingStage implements PipelineStage {
  name = 'Spatial Processing'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { laps, dto, track } = context

      if (!laps || laps.length === 0) {
        return {
          success: false,
          error: 'No laps available for spatial processing',
        }
      }

      // Process each lap to add spatial buckets
      const processedLaps: Lap[] = []

      for (const lap of laps) {
        try {
          // Create spatial buckets from processed telemetry
          const spatialBuckets = createSpatialBuckets(
            lap.processedTelemetryData
          )

          if (spatialBuckets.length === 0) {
            warnings.push(`Lap ${lap.lapNumber} has no spatial buckets`)
          }

          // Create new lap with spatial buckets
          const metadata: LapMetadata = {
            lapId: lap.id,
            lapNumber: lap.lapNumber,
            driverName: lap.driverName,
            trackSessionId: lap.sessionId,
            trackId: lap.trackId,
          }

          const lapWithBuckets = new Lap(
            metadata,
            lap.bounds,
            lap.processedTelemetryData,
            lap.rawTelemetry,
            {
              device: lap.device,
              sessionName: lap.sessionName,
              gyro: lap.gyro,
              accl: lap.accl,
              weather: lap.weather,
              spatialBuckets,
            }
          )

          // Preserve existing analysis results
          lapWithBuckets.breakingPoints = lap.breakingPoints
          lapWithBuckets.slowestPoints = lap.slowestPoints
          lapWithBuckets.topSpeed = lap.topSpeed
          lapWithBuckets.isSelected = lap.isSelected
          lapWithBuckets.isShared = lap.isShared

          processedLaps.push(lapWithBuckets)
        } catch (error) {
          warnings.push(
            `Failed to process spatial buckets for lap ${lap.lapNumber}: ${
              error instanceof Error ? error.message : String(error)
            }`
          )
          // Keep original lap without spatial buckets
          processedLaps.push(lap)
        }
      }

      context.laps = processedLaps

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Spatial processing failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
}
